import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Building2, Users, DollarSign, Globe, Phone, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { apiClient } from '../lib/apiClient';
import { usePlanInfo } from '../hooks/usePlanInfo';
import EnforcementModal from './EnforcementModal';

interface SalesforceAccount {
  id: string;
  name: string;
  type: string;
  industry: string;
  website: string;
  phone: string;
  annual_revenue: number;
  employee_count: number;
  billing_address: {
    street: string;
    city: string;
    state: string;
    country: string;
  };
  description: string;
  last_modified: string;
  is_sample_data: boolean;
}

interface SalesforceAccountListData {
  accounts: SalesforceAccount[];
  summary: {
    total_accounts: number;
    sample_accounts: number;
    real_accounts: number;
    has_more: boolean;
  };
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

interface SalesforceAccountImportModalProps {
  onClose: () => void;
}

const SalesforceAccountImportModal: React.FC<SalesforceAccountImportModalProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const { data: planInfo } = usePlanInfo();
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [enforcementError, setEnforcementError] = useState<any>(null);

  // Fetch Salesforce accounts
  const { data: accountData, isLoading, error, refetch } = useQuery<SalesforceAccountListData>({
    queryKey: ['salesforce-accounts'],
    queryFn: async () => {
      return apiClient.customRequest('/api/integrations/salesforce/accounts');
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });

  // Import selected accounts mutation
  const importMutation = useMutation<any, Error, string[]>({
    mutationFn: async (accountIds: string[]) => {
      return apiClient.customRequest('/api/integrations/salesforce/import-accounts', {
        method: 'POST',
        body: { account_ids: accountIds }
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onClose();
      // Show success message
      alert(`Successfully imported ${data.accounts_imported} of ${data.accounts_requested} selected accounts!`);
    },
    onError: (error: any) => {
      console.error('Import error:', error);
      
      // Check if it's a plan limit error
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        const enforcementError = errorData.error || errorData;
        
        if (enforcementError.error === 'plan_limit_exceeded' || 
            enforcementError.message?.includes('Limit of')) {
          setEnforcementError(enforcementError);
          setShowEnforcementModal(true);
          return;
        }
      }
      
      alert(`Failed to import accounts: ${error.message}`);
    }
  });

  const toggleAccount = (accountId: string) => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(accountId)) {
      newSelected.delete(accountId);
    } else {
      newSelected.add(accountId);
    }
    setSelectedAccounts(newSelected);
  };

  const selectAllRealAccounts = () => {
    if (!accountData) return;
    const realAccountIds = accountData.accounts
      .filter(account => !account.is_sample_data)
      .map(account => account.id);
    setSelectedAccounts(new Set(realAccountIds));
  };

  const selectNone = () => {
    setSelectedAccounts(new Set());
  };

  const handleImport = async () => {
    if (selectedAccounts.size === 0) {
      alert('Please select at least one account to import.');
      return;
    }

    const confirmMessage = `Are you sure you want to import ${selectedAccounts.size} selected accounts?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    importMutation.mutate(Array.from(selectedAccounts));
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getAccountTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'customer': return 'bg-green-100 text-green-800';
      case 'prospect': return 'bg-blue-100 text-blue-800';
      case 'partner': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">Loading Salesforce accounts...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertCircle className="w-5 h-5 mr-2" />
              Error Loading Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Failed to load Salesforce accounts. Please try again.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => refetch()} variant="outline">
                Retry
              </Button>
              <Button onClick={onClose} variant="ghost">
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const realAccounts = accountData?.accounts.filter(account => !account.is_sample_data) || [];
  const sampleAccounts = accountData?.accounts.filter(account => account.is_sample_data) || [];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden shadow-2xl border-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl flex items-center dark:text-gray-100">
                  <Building2 className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400" />
                  Select Salesforce Accounts to Import
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Choose which Salesforce accounts you want to import as accounts
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Selection Controls */}
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllRealAccounts}
                  disabled={importMutation.isPending || realAccounts.length === 0}
                >
                  Select All Real Accounts
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectNone}
                  disabled={importMutation.isPending}
                >
                  Select None
                </Button>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedAccounts.size} of {realAccounts.length} real accounts selected
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <Tabs defaultValue="real" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="real">
                  Real Accounts ({realAccounts.length})
                </TabsTrigger>
                <TabsTrigger value="sample">
                  Sample Data ({sampleAccounts.length})
                </TabsTrigger>
              </TabsList>

              {/* Real Accounts Tab */}
              <TabsContent value="real" className="mt-6">
                <div className="space-y-4 max-h-96 overflow-y-auto px-6">
                  {realAccounts.length === 0 ? (
                    <div className="text-center py-8">
                      <Building2 className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">No real accounts found in your Salesforce org.</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                        You may need to create some accounts in Salesforce first.
                      </p>
                    </div>
                  ) : (
                    realAccounts.map((account) => (
                      <div
                        key={account.id}
                        className={`border rounded-lg p-4 transition-all duration-200 ${
                          selectedAccounts.has(account.id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedAccounts.has(account.id)}
                            onChange={() => toggleAccount(account.id)}
                            disabled={importMutation.isPending}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {account.name}
                              </h3>
                              <Badge className={getAccountTypeColor(account.type)}>
                                {account.type}
                              </Badge>
                            </div>
                            
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="flex items-center text-gray-600 dark:text-gray-400">
                                <Building2 className="w-4 h-4 mr-2" />
                                <span className="truncate">{account.industry}</span>
                              </div>
                              <div className="flex items-center text-gray-600 dark:text-gray-400">
                                <Users className="w-4 h-4 mr-2" />
                                <span>{account.employee_count || 'N/A'} employees</span>
                              </div>
                              <div className="flex items-center text-gray-600 dark:text-gray-400">
                                <DollarSign className="w-4 h-4 mr-2" />
                                <span>{formatCurrency(account.annual_revenue)}</span>
                              </div>
                              <div className="flex items-center text-gray-600 dark:text-gray-400">
                                <Globe className="w-4 h-4 mr-2" />
                                <span className="truncate">{account.website || 'N/A'}</span>
                              </div>
                            </div>

                            {account.description && (
                              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                {account.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Sample Data Tab */}
              <TabsContent value="sample" className="mt-6">
                <div className="space-y-4 max-h-96 overflow-y-auto px-6">
                  {sampleAccounts.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-400 dark:text-green-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">No sample data found!</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                        Your Salesforce org is clean of sample accounts.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-center">
                          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            These are sample/demo accounts from Salesforce. They are filtered out from import selection.
                          </p>
                        </div>
                      </div>
                      {sampleAccounts.map((account) => (
                        <div
                          key={account.id}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 opacity-75"
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              disabled
                              className="h-4 w-4 text-gray-400 border-gray-300 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 truncate">
                                  {account.name}
                                </h3>
                                <Badge variant="secondary" className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  Sample Data
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                This is sample data and will not be imported.
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedAccounts.size > 0 && (
                  <span>
                    {selectedAccounts.size} account{selectedAccounts.size !== 1 ? 's' : ''} selected for import
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={importMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={selectedAccounts.size === 0 || importMutation.isPending}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {importMutation.isPending ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Importing...
                    </>
                  ) : (
                    `Import ${selectedAccounts.size} Account${selectedAccounts.size !== 1 ? 's' : ''}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Enforcement Modal */}
      {showEnforcementModal && (
        <EnforcementModal
          isOpen={showEnforcementModal}
          error={enforcementError}
          onClose={() => setShowEnforcementModal(false)}
        />
      )}
    </>
  );
};

export default SalesforceAccountImportModal;
